import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Loader2,
  Package,
  User,
  MapPin,
  Receipt,
} from "lucide-react";
import { API } from "@/App";

export default function Sales({ user }) {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [form, setForm] = useState({
    product_id: "",
    seller_id: "",
    location_id: "",
    quantity: "1",
    sale_price: "",
    sale_type: "pos",
  });

  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes, productsRes, sellersRes, locationsRes, inventoryRes] = await Promise.all([
        fetch(`${API}/sales`, { credentials: "include" }),
        fetch(`${API}/products`, { credentials: "include" }),
        fetch(`${API}/sellers`, { credentials: "include" }),
        fetch(`${API}/locations`, { credentials: "include" }),
        fetch(`${API}/inventory`, { credentials: "include" }),
      ]);

      if (salesRes.ok) setSales(await salesRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      if (sellersRes.ok) setSellers(await sellersRes.json());
      if (locationsRes.ok) setLocations(await locationsRes.json());
      if (inventoryRes.ok) setInventory(await inventoryRes.json());
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.product_id === productId);
    setSelectedProduct(product);
    setForm({
      ...form,
      product_id: productId,
      sale_price: product?.retail_price?.toString() || "",
    });
  };

  const getProductStock = (productId, locationId) => {
    const inv = inventory.find(i => i.product_id === productId && i.location_id === locationId);
    return inv?.quantity || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_id || !form.location_id || !form.sale_price) {
      toast.error("Please fill all required fields");
      return;
    }

    const stock = getProductStock(form.product_id, form.location_id);
    if (stock < parseInt(form.quantity)) {
      toast.error(`Insufficient stock. Only ${stock} available.`);
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API}/sales`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          quantity: parseInt(form.quantity),
          sale_price: parseFloat(form.sale_price),
          seller_id: form.seller_id || null,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Sale recorded successfully!");
        setDialogOpen(false);
        setForm({
          product_id: "",
          seller_id: "",
          location_id: "",
          quantity: "1",
          sale_price: "",
          sale_type: "pos",
        });
        setSelectedProduct(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to process sale");
      }
    } catch (error) {
      toast.error("Error processing sale");
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  const totalSales = sales.reduce((sum, s) => sum + (s.sale_price * s.quantity), 0);
  const totalProfit = sales.reduce((sum, s) => sum + s.profit, 0);
  const totalCommission = sales.reduce((sum, s) => sum + s.commission_amount, 0);

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="sales-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Sales & POS
            </h1>
            <p className="text-muted-foreground">
              Process sales and track transactions
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-sale-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Sale
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Process Sale</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Product *</Label>
                  <Select
                    value={form.product_id}
                    onValueChange={handleProductSelect}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.product_id} value={p.product_id}>
                          {p.name} - {formatCurrency(p.retail_price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedProduct && (
                  <div className="p-3 bg-muted rounded-sm">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Cost:</span>
                      <span className="font-data">{formatCurrency(selectedProduct.cost_price)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Retail:</span>
                      <span className="font-data">{formatCurrency(selectedProduct.retail_price)}</span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Location *</Label>
                  <Select
                    value={form.location_id}
                    onValueChange={(v) => setForm({ ...form, location_id: v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => {
                        const stock = form.product_id ? getProductStock(form.product_id, l.location_id) : 0;
                        return (
                          <SelectItem key={l.location_id} value={l.location_id}>
                            {l.name} ({stock} in stock)
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      className="rounded-sm font-data"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sale Price *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        step="0.01"
                        value={form.sale_price}
                        onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                        className="rounded-sm font-data pl-8"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Seller (Optional)</Label>
                  <Select
                    value={form.seller_id || "none"}
                    onValueChange={(v) => setForm({ ...form, seller_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="No seller assigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No seller</SelectItem>
                      {sellers.filter(s => s.status === "active").map((s) => (
                        <SelectItem key={s.seller_id} value={s.seller_id}>
                          {s.name} ({s.commission_percent}% commission)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Sale Type</Label>
                  <Select
                    value={form.sale_type}
                    onValueChange={(v) => setForm({ ...form, sale_type: v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pos">POS / In-Store</SelectItem>
                      <SelectItem value="manual">Manual Entry</SelectItem>
                      <SelectItem value="marketplace">Marketplace</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="rounded-sm"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={processing} className="rounded-sm flex-1">
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Complete Sale"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-blue-500/10 flex items-center justify-center">
                <Receipt className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total Sales</p>
                <p className="font-data text-2xl font-semibold">{sales.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Revenue</p>
                <p className="font-data text-2xl font-semibold">{formatCurrency(totalSales)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-purple-500/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Profit</p>
                <p className="font-data text-2xl font-semibold text-emerald-500">{formatCurrency(totalProfit)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-orange-500/10 flex items-center justify-center">
                <User className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Commissions</p>
                <p className="font-data text-2xl font-semibold text-orange-500">{formatCurrency(totalCommission)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sales Table */}
        <Card className="rounded-sm border">
          <CardHeader>
            <CardTitle className="text-base font-heading">Recent Sales</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 skeleton-pulse" />
                ))}
              </div>
            ) : sales.length === 0 ? (
              <div className="p-8 text-center">
                <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-4">No sales recorded yet</p>
                <Button onClick={() => setDialogOpen(true)} className="rounded-sm">
                  Record your first sale
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Seller</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="font-data text-right">Qty</TableHead>
                    <TableHead className="font-data text-right">Amount</TableHead>
                    <TableHead className="font-data text-right">Profit</TableHead>
                    <TableHead className="font-data text-right">Commission</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sales.map((sale) => (
                    <TableRow key={sale.sale_id} className="data-row">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span>{sale.product?.name || "Unknown"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {sale.seller?.name || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          {sale.location?.name || "Unknown"}
                        </div>
                      </TableCell>
                      <TableCell className="font-data text-right">{sale.quantity}</TableCell>
                      <TableCell className="font-data text-right">
                        {formatCurrency(sale.sale_price * sale.quantity)}
                      </TableCell>
                      <TableCell className="font-data text-right text-emerald-500">
                        {formatCurrency(sale.profit)}
                      </TableCell>
                      <TableCell className="font-data text-right text-orange-500">
                        {sale.commission_amount > 0 ? formatCurrency(sale.commission_amount) : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="rounded-sm capitalize">
                          {sale.sale_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-data text-sm text-muted-foreground">
                        {new Date(sale.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
